import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Share2, 
  Play,
  Pause,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Heart,
  Zap,
  Award
} from "lucide-react";
import { MOOD_THEMES } from "../contexts/MoodThemeContext";
import { ShareMood } from "./ShareMood";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mood values for calculations
const MOOD_VALUES = {
  very_happy: 5, happy: 4, relaxed: 4, in_love: 5, sporty: 4, surprised: 3, forgiven: 4,
  neutral: 3, tired: 2, anxious: 2, sad: 2, angry: 1, sick: 1, very_sad: 1
};

// Story slide components
const SlideIntro = ({ userName, weekRange, dominantMood, onNext }) => {
  const theme = MOOD_THEMES[dominantMood] || MOOD_THEMES.neutral;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full flex flex-col items-center justify-center text-center px-8"
      style={{ background: `linear-gradient(180deg, ${theme.primary}20, transparent)` }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
        className="text-7xl mb-6"
      >
        {theme.emoji}
      </motion.div>
      
      <motion.h1
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="font-orbitron text-3xl text-white mb-2"
      >
        Tu Semana Emocional
      </motion.h1>
      
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="text-slate-400 font-rajdhani text-lg mb-8"
      >
        {weekRange}
      </motion.p>
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="flex items-center gap-2 text-neon-cyan"
      >
        <Sparkles className="w-5 h-5" />
        <span className="font-rajdhani">Toca para ver tu historia</span>
      </motion.div>
    </motion.div>
  );
};

const SlideMoodCount = ({ moodCounts, totalMoods }) => {
  // Get top 3 moods
  const topMoods = Object.entries(moodCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full flex flex-col items-center justify-center px-8"
    >
      <motion.h2
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="font-orbitron text-2xl text-white mb-2"
      >
        Registraste
      </motion.h2>
      
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
        className="text-6xl font-orbitron text-neon-cyan mb-2"
      >
        {totalMoods}
      </motion.div>
      
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-slate-400 font-rajdhani text-lg mb-8"
      >
        estados de ánimo
      </motion.p>

      <div className="space-y-4 w-full max-w-xs">
        {topMoods.map(([mood, count], index) => {
          const theme = MOOD_THEMES[mood] || MOOD_THEMES.neutral;
          const percentage = Math.round((count / totalMoods) * 100);
          
          return (
            <motion.div
              key={mood}
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.7 + index * 0.2 }}
              className="flex items-center gap-3"
            >
              <span className="text-3xl">{theme.emoji}</span>
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-white font-rajdhani">{theme.name}</span>
                  <span className="text-slate-400">{percentage}%</span>
                </div>
                <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ delay: 1 + index * 0.2, duration: 0.5 }}
                    className="h-full rounded-full"
                    style={{ background: theme.primary }}
                  />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

const SlideTrend = ({ trend, avgMood, bestDay, worstDay }) => {
  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  const trendColor = trend > 0 ? "text-green-400" : trend < 0 ? "text-red-400" : "text-yellow-400";
  const trendText = trend > 0 ? "¡Mejorando!" : trend < 0 ? "Bajando un poco" : "Estable";
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full flex flex-col items-center justify-center px-8"
    >
      <motion.h2
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="font-orbitron text-2xl text-white mb-6"
      >
        Tu Tendencia
      </motion.h2>
      
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
        className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 ${
          trend > 0 ? "bg-green-500/20" : trend < 0 ? "bg-red-500/20" : "bg-yellow-500/20"
        }`}
      >
        <TrendIcon className={`w-12 h-12 ${trendColor}`} />
      </motion.div>
      
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className={`font-orbitron text-xl ${trendColor} mb-8`}
      >
        {trendText}
      </motion.p>

      <div className="grid grid-cols-2 gap-6 w-full max-w-xs">
        {bestDay && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="bg-green-500/10 rounded-xl p-4 text-center border border-green-500/30"
          >
            <span className="text-3xl">{MOOD_THEMES[bestDay.mood]?.emoji || "😊"}</span>
            <p className="text-xs text-green-400 font-rajdhani mt-2">Mejor día</p>
            <p className="text-white text-sm">{bestDay.dayName}</p>
          </motion.div>
        )}
        
        {worstDay && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="bg-red-500/10 rounded-xl p-4 text-center border border-red-500/30"
          >
            <span className="text-3xl">{MOOD_THEMES[worstDay.mood]?.emoji || "😔"}</span>
            <p className="text-xs text-red-400 font-rajdhani mt-2">Día difícil</p>
            <p className="text-white text-sm">{worstDay.dayName}</p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

const SlideStreak = ({ streak, totalXP, level, achievements }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full flex flex-col items-center justify-center px-8"
    >
      <motion.h2
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="font-orbitron text-2xl text-white mb-6"
      >
        Tus Logros
      </motion.h2>
      
      <div className="grid grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-neon-cyan/10 rounded-xl p-4 text-center border border-neon-cyan/30"
        >
          <Zap className="w-8 h-8 text-neon-yellow mx-auto mb-2" />
          <p className="font-orbitron text-2xl text-white">{totalXP}</p>
          <p className="text-xs text-slate-400 font-rajdhani">XP</p>
        </motion.div>
        
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-neon-purple/10 rounded-xl p-4 text-center border border-neon-purple/30"
        >
          <Award className="w-8 h-8 text-neon-purple mx-auto mb-2" />
          <p className="font-orbitron text-2xl text-white">{level}</p>
          <p className="text-xs text-slate-400 font-rajdhani">Nivel</p>
        </motion.div>
        
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.7 }}
          className="bg-orange-500/10 rounded-xl p-4 text-center border border-orange-500/30"
        >
          <Calendar className="w-8 h-8 text-orange-400 mx-auto mb-2" />
          <p className="font-orbitron text-2xl text-white">{streak}</p>
          <p className="text-xs text-slate-400 font-rajdhani">Racha</p>
        </motion.div>
      </div>

      {achievements.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-center"
        >
          <p className="text-slate-400 text-sm mb-3">Logros desbloqueados</p>
          <div className="flex justify-center gap-2 flex-wrap">
            {achievements.slice(0, 5).map((achievement, i) => (
              <motion.span
                key={achievement.id}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1 + i * 0.1 }}
                className="text-2xl"
                title={achievement.name}
              >
                {achievement.icon}
              </motion.span>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const SlideOutro = ({ dominantMood, motivationalMessage, onShare }) => {
  const theme = MOOD_THEMES[dominantMood] || MOOD_THEMES.neutral;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full flex flex-col items-center justify-center px-8 text-center"
      style={{ background: `linear-gradient(0deg, ${theme.primary}20, transparent)` }}
    >
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.3, type: "spring" }}
        className="mb-6"
      >
        <Heart className="w-16 h-16 text-neon-pink" fill="currentColor" />
      </motion.div>
      
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="font-orbitron text-2xl text-white mb-4"
      >
        {motivationalMessage}
      </motion.h2>
      
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="text-slate-400 font-rajdhani mb-8"
      >
        Sigue registrando para ver cómo evolucionas
      </motion.p>
      
      <motion.button
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.9 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onShare}
        data-testid="share-mood-story-btn"
        className="relative z-30 flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple text-white font-rajdhani font-semibold"
      >
        <Share2 className="w-5 h-5" />
        Compartir mi historia
      </motion.button>
    </motion.div>
  );
};

// Progress bar component
const StoryProgress = ({ currentSlide, totalSlides, progress, isPaused }) => {
  return (
    <div className="absolute top-4 left-4 right-4 flex gap-1.5 z-20">
      {Array.from({ length: totalSlides }).map((_, index) => (
        <div
          key={index}
          className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden"
        >
          <motion.div
            className="h-full bg-white rounded-full"
            initial={{ width: index < currentSlide ? "100%" : "0%" }}
            animate={{
              width: index < currentSlide ? "100%" : 
                     index === currentSlide ? `${progress}%` : "0%"
            }}
            transition={{ duration: 0.1 }}
          />
        </div>
      ))}
    </div>
  );
};

// Main MoodStory component
export const MoodStory = ({ onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [storyData, setStoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  
  const intervalRef = useRef(null);
  const SLIDE_DURATION = 5000; // 5 seconds per slide
  const totalSlides = 5;

  // Fetch story data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [moodsRes, statsRes] = await Promise.all([
          axios.get(`${API}/moods?limit=50`),
          axios.get(`${API}/gamification/stats`)
        ]);

        const moods = moodsRes.data;
        const stats = statsRes.data;

        // Get moods from last 7 days
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        const weekMoods = moods.filter(m => new Date(m.timestamp) >= oneWeekAgo);
        
        // Calculate mood counts
        const moodCounts = {};
        weekMoods.forEach(m => {
          moodCounts[m.mood] = (moodCounts[m.mood] || 0) + 1;
        });

        // Find dominant mood
        const dominantMood = Object.entries(moodCounts)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || "neutral";

        // Calculate trend
        const firstHalf = weekMoods.slice(Math.floor(weekMoods.length / 2));
        const secondHalf = weekMoods.slice(0, Math.floor(weekMoods.length / 2));
        
        const avgFirst = firstHalf.reduce((sum, m) => sum + (MOOD_VALUES[m.mood] || 3), 0) / (firstHalf.length || 1);
        const avgSecond = secondHalf.reduce((sum, m) => sum + (MOOD_VALUES[m.mood] || 3), 0) / (secondHalf.length || 1);
        const trend = avgSecond - avgFirst;

        // Find best and worst days
        const dayNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
        let bestDay = null, worstDay = null;
        
        weekMoods.forEach(m => {
          const value = MOOD_VALUES[m.mood] || 3;
          const date = new Date(m.timestamp);
          const dayName = dayNames[date.getDay()];
          
          if (!bestDay || value > MOOD_VALUES[bestDay.mood]) {
            bestDay = { mood: m.mood, dayName };
          }
          if (!worstDay || value < MOOD_VALUES[worstDay.mood]) {
            worstDay = { mood: m.mood, dayName };
          }
        });

        // Format week range
        const today = new Date();
        const weekStart = new Date(oneWeekAgo);
        const formatDate = (d) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
        const weekRange = `${formatDate(weekStart)} - ${formatDate(today)}`;

        // Motivational messages based on trend
        const messages = {
          positive: ["¡Vas muy bien!", "¡Sigue así!", "¡Tu energía brilla!"],
          negative: ["Cada día es una nueva oportunidad", "Lo estás haciendo genial", "Mañana será mejor"],
          neutral: ["Mantén el equilibrio", "Consistencia es clave", "Sigues adelante"]
        };
        
        const messageType = trend > 0.3 ? "positive" : trend < -0.3 ? "negative" : "neutral";
        const motivationalMessage = messages[messageType][Math.floor(Math.random() * 3)];

        setStoryData({
          weekRange,
          dominantMood,
          moodCounts,
          totalMoods: weekMoods.length,
          trend,
          bestDay,
          worstDay,
          streak: stats.streak || 0,
          totalXP: stats.xp || 0,
          level: stats.level || 1,
          achievements: stats.achievements || [],
          motivationalMessage
        });
      } catch (error) {
        console.error("Error fetching story data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-progress timer
  useEffect(() => {
    if (loading || isPaused || showShare) return;

    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (currentSlide < totalSlides - 1) {
            setCurrentSlide(s => s + 1);
            return 0;
          } else {
            clearInterval(intervalRef.current);
            return 100;
          }
        }
        return prev + (100 / (SLIDE_DURATION / 100));
      });
    }, 100);

    return () => clearInterval(intervalRef.current);
  }, [currentSlide, loading, isPaused, showShare]);

  // Navigation handlers
  const goToSlide = useCallback((direction) => {
    setProgress(0);
    if (direction === "next" && currentSlide < totalSlides - 1) {
      setCurrentSlide(s => s + 1);
    } else if (direction === "prev" && currentSlide > 0) {
      setCurrentSlide(s => s - 1);
    }
  }, [currentSlide]);

  const handleTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeft = x < rect.width / 3;
    
    if (isLeft) {
      goToSlide("prev");
    } else {
      goToSlide("next");
    }
  };

  const handleShare = () => {
    setShowShare(true);
    setIsPaused(true);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-void z-50 flex items-center justify-center"
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin mb-4" />
          <p className="text-slate-400 font-rajdhani">Preparando tu historia...</p>
        </div>
      </motion.div>
    );
  }

  if (!storyData || storyData.totalMoods === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-void z-50 flex items-center justify-center p-8"
      >
        <div className="text-center">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="font-orbitron text-xl text-white mb-2">Sin datos aún</h2>
          <p className="text-slate-400 font-rajdhani mb-6">
            Registra algunos estados de ánimo esta semana para ver tu historia
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/50 font-rajdhani"
          >
            Entendido
          </button>
        </div>
      </motion.div>
    );
  }

  const slides = [
    <SlideIntro 
      key="intro"
      weekRange={storyData.weekRange}
      dominantMood={storyData.dominantMood}
    />,
    <SlideMoodCount 
      key="count"
      moodCounts={storyData.moodCounts}
      totalMoods={storyData.totalMoods}
    />,
    <SlideTrend 
      key="trend"
      trend={storyData.trend}
      bestDay={storyData.bestDay}
      worstDay={storyData.worstDay}
    />,
    <SlideStreak 
      key="streak"
      streak={storyData.streak}
      totalXP={storyData.totalXP}
      level={storyData.level}
      achievements={storyData.achievements}
    />,
    <SlideOutro 
      key="outro"
      dominantMood={storyData.dominantMood}
      motivationalMessage={storyData.motivationalMessage}
      onShare={handleShare}
    />
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-void z-50"
      data-testid="mood-story-container"
    >
      {/* Progress bar */}
      <StoryProgress 
        currentSlide={currentSlide}
        totalSlides={totalSlides}
        progress={progress}
        isPaused={isPaused}
      />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-12 right-4 z-20 p-2 rounded-full bg-black/50 text-white"
        data-testid="close-story-btn"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Pause/Play indicator */}
      {isPaused && !showShare && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="bg-black/50 rounded-full p-4">
            <Pause className="w-8 h-8 text-white" />
          </div>
        </div>
      )}

      {/* Navigation areas */}
      <div 
        className="absolute inset-0 z-10 flex"
        onClick={handleTap}
        onMouseDown={() => setIsPaused(true)}
        onMouseUp={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        <div className="w-1/3 h-full" />
        <div className="w-2/3 h-full" />
      </div>

      {/* Slide content */}
      <div className="h-full pt-12">
        <AnimatePresence mode="wait">
          {slides[currentSlide]}
        </AnimatePresence>
      </div>

      {/* Share modal - ShareMood uses createPortal, so render directly */}
      <AnimatePresence>
        {showShare && (
          <ShareMood 
            mood={storyData.dominantMood}
            note={`Mi semana emocional: ${storyData.totalMoods} registros, nivel ${storyData.level} con ${storyData.totalXP} XP 🎯`}
            onClose={() => { setShowShare(false); setIsPaused(false); }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Button to trigger story
export const MoodStoryButton = ({ onClick }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-neon-pink/20 to-neon-purple/20 border border-neon-pink/30 text-white font-rajdhani hover:border-neon-pink/50 transition-all"
      data-testid="mood-story-btn"
    >
      <Play className="w-4 h-4 text-neon-pink" fill="currentColor" />
      <span>Mi Historia</span>
    </motion.button>
  );
};

export default MoodStory;
